/**
 * Match detail page — public, anonymous.
 *
 * Top half: match header, live events, lineups, stats.
 * Bottom half: SmartKick AI prediction (public) + locked "Odds Comparison"
 * card that funnels to VIP free trial — the conversion focus of the page.
 */
import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useScoresNavigate } from './scoresPath';
import { ArrowLeft, Loader2, Bell, BellRing, Zap, TrendingUp, Clock, MapPin, Users, Activity as ActivityIcon, Newspaper, ExternalLink, ArrowRight, Play, Youtube, Star, ArrowUp, ArrowDown, Share2, X, Swords } from 'lucide-react';
import { toast } from 'sonner';
import { useFavoriteMatches } from './useScoresFavorites';
import { startMatchLiveActivity, stopMatchLiveActivity } from './liveActivities';
import PitchLineup from './PitchLineup';
import BroadcasterBadges from './BroadcasterBadges';
import LiveGoalHighlights from './LiveGoalHighlights';
import { openExternalUrl, shareNative } from '../utils/native';
import { usePullToRefresh } from './usePullToRefresh';
import { Drawer, DrawerContent, DrawerClose } from '../components/ui/drawer';
import MatchCommentary from './MatchCommentary';
import MatchChat from './MatchChat';
import MatchH2H from './MatchH2H';
import MatchMedia from './MatchMedia';
import { ListOrdered, MessageSquare, ChevronDown, Check } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const VIP_TRIAL_URL = 'https://smartkick.co.uk/free-trial';

// Map the model's result code to a human-friendly pick label. Falls back to the
// raw value ('LOCKED' for anonymous/free users) so the funnel card is unchanged.
const pickLabel = (p) => {
  if (!p) return '-';
  if (p.predicted_result === 'HOME_WIN') return p.home_team || 'Home';
  if (p.predicted_result === 'AWAY_WIN') return p.away_team || 'Away';
  if (p.predicted_result === 'DRAW') return 'Draw';
  return p.predicted_result;
};

// Red-card marker for the match header — matches the live-scores list style
// (a small red rectangle + count when there's more than one).
const RedCardBadge = ({ count, teamId }) => (
  <span
    className="shrink-0 inline-flex items-center gap-0.5"
    title={`${count} red card${count > 1 ? 's' : ''}`}
    aria-label={`${count} red card${count > 1 ? 's' : ''}`}
    data-testid={`detail-red-card-${teamId ?? 'team'}`}
  >
    <span className="inline-block w-[11px] h-[15px] rounded-[2px] bg-[#e11d48] shadow-[0_0_4px_rgba(225,29,72,0.6)]" />
    {count > 1 && <span className="text-[10px] font-bold text-[#e11d48]">{count}</span>}
  </span>
);


/**
 * Resilient JSON fetch with a 25s timeout. Returns `null` on any
 * failure so the caller can keep its previous state instead of blowing
 * up with an undefined response. Matches the helper used in the main
 * app's LiveScoresPublic.
 */
const robustFetch = async (url, timeoutMs = 25000) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    // Forward the SmartKick auth token (set by the main Predictions app on the
    // same domain) so logged-in subscribers see UNLOCKED predictions inside the
    // Live Scores section. Anonymous visitors still get the locked funnel card.
    let authToken = null;
    try { authToken = localStorage.getItem('token'); } catch { /* ignore */ }
    const headers = authToken ? { Authorization: `Bearer ${authToken}` } : undefined;
    const r = await fetch(url, { signal: controller.signal, headers });
    if (!r.ok) return null;
    return await r.json();
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
};

const ScoresMatchDetail = ({ onPredictionsCtaClick }) => {
  const { id } = useParams();
  const navigate = useScoresNavigate();
  const [detail, setDetail] = useState(null);
  const [prediction, setPrediction] = useState(null);
  const [news, setNews] = useState(null);
  const [odds, setOdds] = useState(null);
  const [highlights, setHighlights] = useState(null);
  const [showVideo, setShowVideo] = useState(false);
  const [playerStats, setPlayerStats] = useState(null);
  const [showSubEvents, setShowSubEvents] = useState(true);
  const [tab, setTab] = useState(() => {
    try {
      const t = new URLSearchParams(window.location.search).get('tab');
      return ['commentary', 'h2h', 'chat', 'news'].includes(t) ? t : 'match';
    } catch { return 'match'; }
  });
  const [loading, setLoading] = useState(true);
  const [fetchFailed, setFetchFailed] = useState(false);
  const favMatches = useFavoriteMatches();
  const notifyOn = favMatches.isFavorite(Number(id));

  // Universal back handler. `navigate(-1)` silently no-ops when there's
  // no SPA history (e.g. user opened the URL from a push notification or
  // a shared link), leaving them stuck. Fall back to the home page.
  const goBack = () => {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('');
    }
  };

  const loadAll = () => {
    setLoading(true);
    setFetchFailed(false);
    Promise.all([
      robustFetch(`${API_URL}/api/live-scores/match/${id}`),
      robustFetch(`${API_URL}/api/predictions/match/${id}`),
      robustFetch(`${API_URL}/api/live-scores/match/${id}/news`),
      robustFetch(`${API_URL}/api/live-scores/match/${id}/odds`),
    ]).then(([d, p, n, o]) => {
      if (d && d.home_team) {
        setDetail(d);
      } else {
        setFetchFailed(true);
      }
      if (p) setPrediction(p);
      if (n) setNews(n);
      if (o) setOdds(o);
      setLoading(false);
    });
  };

  // Silent re-fetch for pull-to-refresh — updates everything in place without
  // blanking the page (no full-screen "Loading…" spinner).
  const refresh = useCallback(async () => {
    const [d, p, n, o, ps] = await Promise.all([
      robustFetch(`${API_URL}/api/live-scores/match/${id}`),
      robustFetch(`${API_URL}/api/predictions/match/${id}`),
      robustFetch(`${API_URL}/api/live-scores/match/${id}/news`),
      robustFetch(`${API_URL}/api/live-scores/match/${id}/odds`),
      robustFetch(`${API_URL}/api/live-scores/match/${id}/player-stats`),
    ]);
    if (d && d.home_team) setDetail(d);
    if (p) setPrediction(p);
    if (n) setNews(n);
    if (o) setOdds(o);
    if (ps && Array.isArray(ps.teams)) setPlayerStats(ps);
  }, [id]);

  const { containerRef, pullY, refreshing } = usePullToRefresh(refresh);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setFetchFailed(false);
    Promise.all([
      robustFetch(`${API_URL}/api/live-scores/match/${id}`),
      robustFetch(`${API_URL}/api/predictions/match/${id}`),
      robustFetch(`${API_URL}/api/live-scores/match/${id}/news`),
      robustFetch(`${API_URL}/api/live-scores/match/${id}/odds`),
    ]).then(([d, p, n, o]) => {
      if (!alive) return;
      if (d && d.home_team) {
        setDetail(d);
      } else {
        setFetchFailed(true);
      }
      setPrediction(p);
      setNews(n);
      setOdds(o);
      setLoading(false);
    });
    return () => { alive = false; };
  }, [id]);

  // Fetch YouTube highlights once we know the match is finished.
  // The backend gates on league + status, so we just trigger after detail loads.
  useEffect(() => {
    if (!detail || !['FT', 'AET', 'PEN'].includes(detail.status_short)) return;
    let alive = true;
    robustFetch(`${API_URL}/api/live-scores/match/${id}/highlights`).then((h) => {
      if (alive) setHighlights(h);
    });
    return () => { alive = false; };
  }, [id, detail]);

  // Per-player live stats — fetch once detail is known, then poll every 30s
  // while the match is in play so shots / ratings update live.
  useEffect(() => {
    if (!detail) return;
    let alive = true;
    const fetchPS = () => robustFetch(`${API_URL}/api/live-scores/match/${id}/player-stats`)
      .then((d) => { if (alive && d && Array.isArray(d.teams)) setPlayerStats(d); });
    fetchPS();
    const live = ['1H', '2H', 'ET', 'P', 'HT', 'BT'].includes(detail.status_short);
    let timer;
    if (live) timer = setInterval(fetchPS, 30000);
    return () => { alive = false; if (timer) clearInterval(timer); };
  }, [id, detail?.status_short]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-refresh the whole match (score, events, stats, lineups, odds) every
  // 30s while the match is in play — no user action needed.
  useEffect(() => {
    if (!detail) return;
    const live = ['1H', '2H', 'ET', 'P', 'HT', 'BT'].includes(detail.status_short);
    if (!live) return;
    const timer = setInterval(() => { refresh(); }, 30000);
    return () => clearInterval(timer);
  }, [detail?.status_short, refresh]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Live in-play 1X2 odds — while the match is in play, poll the odds endpoint
  // with ?live=1 (short 45s server cache) so the home/draw/away prices update.
  useEffect(() => {
    if (!detail) return;
    const live = ['1H', '2H', 'ET', 'P', 'HT', 'BT'].includes(detail.status_short);
    if (!live) return;
    let alive = true;
    const poll = () => robustFetch(`${API_URL}/api/live-scores/match/${id}/odds?live=1`)
      .then((o) => { if (alive && o && (o.bookmakers || []).length) setOdds(o); });
    poll();
    const t = setInterval(poll, 40000);
    return () => { alive = false; clearInterval(t); };
  }, [id, detail?.status_short]);  // eslint-disable-line react-hooks/exhaustive-deps

  const sharePrediction = async () => {
    if (!detail) return;
    const imgUrl = `${API_URL}/api/live-scores/match/${detail.fixture_id}/prediction-card.png`;
    const text = `🔮 SmartKick AI pick: ${detail.home_team} v ${detail.away_team} · Get AI predictions at smartkick.co.uk`;
    if (typeof navigator !== 'undefined' && navigator.canShare) {
      try {
        const res = await fetch(imgUrl);
        if (res.ok) {
          const blob = await res.blob();
          const file = new File([blob], 'smartkick-prediction.png', { type: 'image/png' });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file], title: 'SmartKick AI Prediction', text });
            return;
          }
        }
      } catch { /* fall through */ }
    }
    const r = await shareNative({ title: 'SmartKick AI Prediction', text, url: 'https://smartkick.co.uk/live-scores', dialogTitle: 'Share AI pick' });
    if (r.method === 'clipboard') toast.success('Copied to clipboard');
    else if (!r.shared && !r.cancelled) toast('Sharing not available on this device');
  };

  const toggleNotify = () => {
    const matchInfo = detail ? {
      fixture_id: Number(id),
      home_team: detail.home_team,
      away_team: detail.away_team,
      home_logo: detail.home_logo,
      away_logo: detail.away_logo,
      league_name: detail.league_name,
      date: detail.date,
      status_short: detail.status_short,
      home_score: detail.home_score ?? detail.goals_home,
      away_score: detail.away_score ?? detail.goals_away,
    } : null;
    favMatches.toggle(matchInfo || Number(id));
    if (!notifyOn) {
      if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission().catch(() => {});
      }
      // Start an iOS Live Activity (Lock Screen match card) — no-op off iOS.
      if (matchInfo) startMatchLiveActivity(matchInfo);
      toast.success('Alerts on', { description: 'We\'ll notify you on goals & key moments.' });
    } else {
      stopMatchLiveActivity(Number(id));
      toast('Alerts off');
    }
  };

  if (loading && !detail) {
    return (
      <div className="px-4">
        <div className="sticky top-14 z-30 bg-[var(--sk-bg)]/95 backdrop-blur-xl border-b border-[var(--sk-border)] py-2.5 flex items-center gap-3 -mx-4 px-3">
          <button onClick={goBack} className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center" aria-label="Back" data-testid="detail-back-loading">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <span className="text-[12px] text-zinc-400">Loading match…</span>
        </div>
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-accent" /></div>
      </div>
    );
  }
  if (!detail) {
    return (
      <div className="px-4">
        <div className="sticky top-14 z-30 bg-[var(--sk-bg)]/95 backdrop-blur-xl border-b border-[var(--sk-border)] py-2.5 flex items-center gap-3 -mx-4 px-3">
          <button onClick={goBack} className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center" aria-label="Back" data-testid="detail-back-error">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <span className="text-[12px] text-zinc-400">Match unavailable</span>
        </div>
        <div className="py-16 text-center" data-testid="scores-detail-error">
          <p className="text-zinc-300 text-sm font-bold mb-1">{fetchFailed ? "Couldn't load match" : 'Match not found'}</p>
          <p className="text-zinc-500 text-xs mb-5">{fetchFailed ? 'Likely a slow connection — try again.' : 'It may have been cancelled or moved.'}</p>
          <div className="flex items-center justify-center gap-2">
            {fetchFailed && (
              <button
                onClick={loadAll}
                className="px-5 py-2 bg-accent hover:opacity-90 rounded-full text-xs font-bold transition-opacity text-black"
                data-testid="scores-detail-retry"
              >
                Retry
              </button>
            )}
            <button onClick={goBack} className="px-5 py-2 bg-white/10 hover:bg-white/15 rounded-full text-xs font-bold transition-colors">
              Back to matches
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isLive = ['1H', '2H', 'ET', 'P', 'HT'].includes(detail.status_short);
  const isFinished = ['FT', 'AET', 'PEN'].includes(detail.status_short);
  const kickoff = detail.date ? new Date(detail.date) : null;

  // Red-card counts per team, from the loaded match events (straight reds AND
  // second yellows). Shown on the header just like the live-scores list.
  const countReds = (teamId, teamName) =>
    (detail.events || []).filter(
      (e) =>
        e.type === 'Card' &&
        /red card|second yellow/i.test(e.detail || '') &&
        (e.team_id === teamId || (teamName && e.team === teamName)),
    ).length;
  const homeReds = countReds(detail.home_id, detail.home_team);
  const awayReds = countReds(detail.away_id, detail.away_team);

  return (
    <div className="pb-4" data-testid="scores-match-detail" ref={containerRef}>
      {/* Pull-to-refresh indicator — grows/spins as the user pulls down */}
      <div
        className="flex items-center justify-center overflow-hidden"
        style={{ height: pullY, transition: pullY === 0 ? 'height 0.25s ease' : 'none' }}
        data-testid="ptr-indicator"
      >
        <Loader2
          className={`w-5 h-5 text-accent ${refreshing ? 'animate-spin' : ''}`}
          style={{ opacity: Math.min(1, pullY / 50), transform: refreshing ? 'none' : `rotate(${pullY * 3}deg)` }}
        />
      </div>
      {/* Header */}
      <div className="sticky top-14 z-30 bg-[var(--sk-bg)]/95 backdrop-blur-xl border-b border-[var(--sk-border)] px-3 py-2.5 flex items-center gap-3">
        <button onClick={goBack} className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center" aria-label="Back" data-testid="detail-back">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0">
          {detail.league_logo && <img src={detail.league_logo} alt="" className="inline w-4 h-4 mr-1.5 object-contain align-middle" />}
          <span className="text-[12px] text-zinc-400 truncate align-middle">{detail.league_name}</span>
        </div>
        <button onClick={toggleNotify} className={`w-9 h-9 rounded-full flex items-center justify-center ${notifyOn ? 'bg-accent-soft text-accent' : 'bg-white/5 text-zinc-400'}`} data-testid="detail-notify">
          {notifyOn ? <BellRing className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
        </button>
      </div>

      {/* Score panel */}
      <div className="px-4 pt-5 pb-4 text-center">
        <div className="grid grid-cols-3 items-center gap-2 mb-3">
          <div className="flex flex-col items-center gap-1.5">
            {detail.home_logo && <img src={detail.home_logo} alt="" className="w-12 h-12 object-contain" />}
            <div className="flex items-center gap-1.5">
              <div className="text-[12px] text-white text-center px-1 leading-tight">{detail.home_team}</div>
              {homeReds > 0 && <RedCardBadge count={homeReds} teamId={detail.home_id} />}
            </div>
          </div>
          <div className="flex flex-col items-center">
            {isLive ? (
              <div className="text-[10px] font-black text-[var(--sk-live)] mb-1 flex items-center gap-1"><span className="live-dot" />{detail.status_short === 'HT' ? 'HT' : `${detail.elapsed}'`}</div>
            ) : isFinished ? (
              <div className="text-[10px] font-black text-zinc-500 mb-1">{detail.status_short}</div>
            ) : kickoff ? (
              <div className="text-[10px] font-black text-zinc-500 mb-1">{kickoff.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</div>
            ) : null}
            <div className="text-3xl font-black text-white tabular-nums">
              {detail.home_goals ?? '-'} : {detail.away_goals ?? '-'}
            </div>
          </div>
          <div className="flex flex-col items-center gap-1.5">
            {detail.away_logo && <img src={detail.away_logo} alt="" className="w-12 h-12 object-contain" />}
            <div className="flex items-center gap-1.5">
              <div className="text-[12px] text-white text-center px-1 leading-tight">{detail.away_team}</div>
              {awayReds > 0 && <RedCardBadge count={awayReds} teamId={detail.away_id} />}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-center gap-3 text-[10px] text-zinc-500 flex-wrap">
          {detail.venue && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{detail.venue}</span>}
          {kickoff && !isLive && !isFinished && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{kickoff.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}</span>}
        </div>
        {detail.broadcasters && detail.broadcasters.length > 0 && (
          <div className="mt-2 flex items-center justify-center">
            <BroadcasterBadges broadcasters={detail.broadcasters} />
          </div>
        )}
      </div>

      {/* Tab bar — Match / Commentary / Chat / News (horizontally scrollable) */}
      <div className="sticky top-[6.5rem] z-20 bg-[var(--sk-bg)]/95 backdrop-blur border-b border-[var(--sk-border)]">
        <div className="flex gap-2 px-3 py-2 overflow-x-auto no-scrollbar">
          {[
            { id: 'match', label: 'Match', icon: ActivityIcon },
            { id: 'commentary', label: 'Commentary', icon: ListOrdered },
            { id: 'h2h', label: 'H2H', icon: Swords },
            { id: 'chat', label: 'Chat', icon: MessageSquare },
            { id: 'news', label: 'News', icon: Newspaper },
          ].map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`match-tab-${t.id}`}
                className={`shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-bold transition-colors border ${
                  active ? 'bg-accent-soft text-accent border-accent/40' : 'bg-white/[0.03] text-zinc-400 border-transparent'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {tab === 'commentary' && (
        <MatchCommentary events={detail.events || []} homeTeam={detail.home_team} awayTeam={detail.away_team} homeId={detail.home_id} />
      )}
      {tab === 'h2h' && (
        <MatchH2H fixtureId={detail.fixture_id || id} homeTeam={detail.home_team} awayTeam={detail.away_team} homeLogo={detail.home_logo} awayLogo={detail.away_logo} />
      )}
      {tab === 'chat' && (
        <MatchChat matchId={detail.fixture_id || id} homeTeam={detail.home_team} awayTeam={detail.away_team} homeLogo={detail.home_logo} awayLogo={detail.away_logo} />
      )}
      {tab === 'news' && <MatchMedia news={news} />}

      {/* AI Prediction (public) + Odds Lock (funnel) */}
      <div className={`px-3 space-y-3 ${tab === 'match' ? '' : 'hidden'}`}>
        {/* Live goal clips (in-play only, polls every 45s) */}
        <LiveGoalHighlights
          fixtureId={detail.fixture_id}
          status={detail.status_short}
          goalCount={(detail.events || []).filter((e) => e.type === 'Goal').length}
        />

        {/* YouTube Match Highlights — only when match is finished + supported league */}
        <HighlightsCard
          highlights={highlights}
          isFinished={isFinished}
          homeTeam={detail.home_team}
          awayTeam={detail.away_team}
          showVideo={showVideo}
          onPlay={() => setShowVideo(true)}
        />

        {prediction && prediction.predicted_result && (
          <button
            type="button"
            onClick={() => onPredictionsCtaClick?.()}
            className="w-full text-left rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/8 via-emerald-500/3 to-transparent p-4 hover:from-emerald-500/15 hover:border-emerald-500/50 transition-colors"
            data-testid="ai-prediction-card"
          >
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-lg bg-emerald-500 flex items-center justify-center"><Zap className="w-3.5 h-3.5 text-white" strokeWidth={2.5} /></div>
              <div className="text-[10px] font-black tracking-[0.18em] uppercase text-emerald-400">SmartKick AI · Prediction</div>
              <ArrowRight className="ml-auto w-3.5 h-3.5 text-emerald-400" />
            </div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <Stat label="Pick" value={pickLabel(prediction)} />
              <Stat label="Score" value={prediction.predicted_score || '-'} />
              <Stat label="Confidence" value={`${prediction.confidence ?? 0}%`} highlight />
              <Stat label="BTTS" value={`${prediction.btts_probability ?? 0}%`} />
            </div>
            {prediction.ai_reasoning && (
              <p className="text-[11px] text-zinc-400 leading-relaxed">{prediction.ai_reasoning.slice(0, 220)}{prediction.ai_reasoning.length > 220 ? '…' : ''}</p>
            )}
            <div className="mt-3 pt-3 border-t border-emerald-500/20 text-[11px] font-bold text-emerald-300 flex items-center justify-center gap-1.5">
              {prediction.predicted_result === 'LOCKED'
                ? 'Get unlocked predictions on SmartKick — 14-day free trial'
                : 'View full analysis & player props on SmartKick'}
              <ArrowRight className="w-3 h-3" />
            </div>
          </button>
        )}

        {prediction && prediction.predicted_result && (
          <button
            type="button"
            onClick={sharePrediction}
            className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 text-[12px] font-bold transition-colors"
            data-testid="share-prediction-btn"
          >
            <Share2 className="w-3.5 h-3.5" />
            Share this AI pick
          </button>
        )}

        {/* Odds Comparison — free, shown on every match */}
        <OddsComparisonCard odds={odds} isLive={isLive} onCtaClick={onPredictionsCtaClick} />

        {/* Live Stats — possession, shots, corners (rendered when in-play) */}
        <LiveStatsCard stats={detail.statistics || []} isLive={isLive} isFinished={isFinished} homeTeamName={detail.home_team} awayTeamName={detail.away_team} />

        {/* Per-player live stats — ratings, shots, shots on target, passes,
            defence. Live during play, full stats after FT. */}
        <PlayerStatsCard data={playerStats} detail={detail} isLive={isLive} isFinished={isFinished} />

        {/* Live match tracker — events timeline (always shown; pre-match
            shows a friendly placeholder so users know it'll fill in live) */}
        <div className="rounded-2xl bg-[var(--sk-surface)] p-4" data-testid="match-tracker">
          <div className="flex items-center gap-1.5 mb-3">
            <ActivityIcon className="w-3.5 h-3.5 text-accent" />
            <div className="text-[10px] font-black uppercase tracking-wider text-zinc-500">Match Tracker</div>
            {isLive && <span className="ml-auto flex items-center gap-1 text-[10px] font-black text-[var(--sk-live)]"><span className="live-dot" /> LIVE</span>}
          </div>
          {(detail.events?.length ?? 0) > 0 ? (
            <>
              <div className="space-y-3" data-testid="tracker-timeline">
                {detail.events
                  .filter((e) => showSubEvents || (e.type || '').toLowerCase() !== 'subst')
                  .map((e, i) => {
                    const isHome = e.team_id === detail.home_id;
                    const isAway = e.team_id === detail.away_id;
                    const minute = `${e.time ?? ''}${e.extra_time ? '+' + e.extra_time : ''}'`;
                    return (
                      <div
                        key={i}
                        className="grid items-center gap-2"
                        style={{ gridTemplateColumns: '1fr auto 1fr' }}
                        data-testid={`tracker-event-${i}`}
                      >
                        {/* Home side (left) */}
                        <div className={`flex items-center justify-end gap-2 text-right min-w-0 ${isHome ? '' : 'invisible'}`}>
                          <div className="min-w-0">
                            <div className="text-white text-[12px] truncate font-medium">{e.player || e.detail}</div>
                            {e.assist && <div className="text-zinc-500 text-[10px] truncate">{e.assist}</div>}
                          </div>
                          <EventIcon type={e.type} detail={e.detail} />
                        </div>
                        {/* Minute (centre) */}
                        <span className="px-2 py-0.5 rounded-full bg-white/[0.06] text-[11px] text-zinc-300 font-mono tabular-nums shrink-0 min-w-[34px] text-center">
                          {minute}
                        </span>
                        {/* Away side (right) */}
                        <div className={`flex items-center gap-2 min-w-0 ${isAway ? '' : 'invisible'}`}>
                          <EventIcon type={e.type} detail={e.detail} />
                          <div className="min-w-0">
                            <div className="text-white text-[12px] truncate font-medium">{e.player || e.detail}</div>
                            {e.assist && <div className="text-zinc-500 text-[10px] truncate">{e.assist}</div>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
              {detail.events.some((e) => (e.type || '').toLowerCase() === 'subst') && (
                <button
                  type="button"
                  onClick={() => setShowSubEvents((s) => !s)}
                  className="mt-3 w-full text-[10px] font-bold uppercase tracking-wider text-zinc-400 hover:text-white py-1.5 rounded bg-white/5 hover:bg-white/10 transition-colors"
                  data-testid="tracker-toggle-subs"
                >
                  {showSubEvents ? 'Hide substitutions' : 'Show substitutions'}
                </button>
              )}
            </>
          ) : isLive ? (
            <div className="text-[11px] text-zinc-500 py-2">Waiting for first event…</div>
          ) : isFinished ? (
            <div className="text-[11px] text-zinc-500 py-2">No events available for this match.</div>
          ) : (
            <div className="text-[11px] text-zinc-500 py-2">Goals, cards & subs will appear here live once the match kicks off.</div>
          )}
        </div>

        {/* Team Lineups — pitch view (or empty-state) */}
        <div data-testid="match-lineups">
          {(detail.lineups?.length ?? 0) >= 2 ? (
            <PitchLineup home={detail.lineups[0]} away={detail.lineups[1]} />
          ) : (
            <div className="rounded-2xl bg-[var(--sk-surface)] p-4">
              <div className="flex items-center gap-1.5 mb-3">
                <Users className="w-3.5 h-3.5 text-accent" />
                <div className="text-[10px] font-black uppercase tracking-wider text-zinc-500">Lineups</div>
              </div>
              <div className="text-[11px] text-zinc-500 py-2">
                {isFinished
                  ? 'Lineups not available for this match.'
                  : 'Confirmed lineups appear ~1 hour before kick-off.'}
              </div>
            </div>
          )}
        </div>

        {/* Match News (related headlines + AI preview) */}
        <div className="rounded-2xl bg-[var(--sk-surface)] p-4" data-testid="match-news">
          <div className="flex items-center gap-1.5 mb-3">
            <Newspaper className="w-3.5 h-3.5 text-accent" />
            <div className="text-[10px] font-black uppercase tracking-wider text-zinc-500">Match News</div>
          </div>
          {news?.ai_preview && (news.ai_preview.headline || news.ai_preview.summary || news.ai_preview.key_battle || news.ai_preview.prediction_teaser) && (
            <div className="mb-3 rounded-lg bg-black/30 p-3 space-y-1.5">
              <div className="flex items-center gap-1.5 mb-0.5">
                <Zap className="w-3 h-3 text-emerald-400" />
                <span className="text-[10px] font-black uppercase tracking-wider text-emerald-400">AI Preview</span>
              </div>
              {news.ai_preview.headline && (
                <div className="text-[12px] font-bold text-white leading-snug">{news.ai_preview.headline}</div>
              )}
              {news.ai_preview.summary && (
                <p className="text-[11px] text-zinc-300 leading-relaxed">{news.ai_preview.summary}</p>
              )}
              {news.ai_preview.key_battle && (
                <p className="text-[11px] text-zinc-400 leading-relaxed"><span className="font-bold text-zinc-300">Key battle: </span>{news.ai_preview.key_battle}</p>
              )}
              {news.ai_preview.prediction_teaser && (
                <p className="text-[11px] text-emerald-300/90 italic leading-relaxed">{news.ai_preview.prediction_teaser}</p>
              )}
            </div>
          )}
          {(news?.headlines?.length ?? 0) > 0 ? (
            <div className="space-y-2">
              {news.headlines.slice(0, 6).map((h, i) => (
                <a
                  key={i}
                  href={h.url || h.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-2 group"
                  data-testid={`news-link-${i}`}
                >
                  <span className="shrink-0 w-1 h-1 rounded-full bg-accent mt-2" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] text-white group-hover:text-accent transition-colors leading-snug">
                      {h.title || h.headline}
                    </div>
                    <div className="text-[10px] text-zinc-500 mt-0.5 flex items-center gap-1">
                      {h.source && <span>{h.source}</span>}
                      <ExternalLink className="w-2.5 h-2.5" />
                    </div>
                  </div>
                </a>
              ))}
            </div>
          ) : !news?.ai_preview ? (
            <div className="text-[11px] text-zinc-500 py-2">No related news found yet — check back closer to kick-off.</div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const Stat = ({ label, value, highlight }) => (
  <div className="rounded-lg bg-black/30 px-2.5 py-2">
    <div className="text-[9px] text-zinc-500 uppercase tracking-wider font-bold mb-0.5">{label}</div>
    <div className={`text-[14px] font-black ${highlight ? 'text-emerald-400' : 'text-white'}`}>{value}</div>
  </div>
);

// ============== Match Highlights Card (YouTube) ==============
// Shown only for finished matches in supported leagues. We render a
// clickable thumbnail until the user taps Play, then swap in the
// official YouTube iframe (lazy embed = no IFrame overhead on scroll).
const HighlightsCard = ({ highlights, isFinished, homeTeam, awayTeam, showVideo, onPlay }) => {
  if (!isFinished) return null;
  if (!highlights || highlights.enabled === false) return null;

  const video = highlights.video;
  const searchUrl = highlights.search_url
    || `https://www.youtube.com/results?search_query=${encodeURIComponent(`${homeTeam} vs ${awayTeam} highlights`)}`;

  // No video yet (just-finished) — show "search on YouTube" fallback.
  if (!video || !video.video_id) {
    return (
      <button
        type="button"
        onClick={() => openExternalUrl(searchUrl)}
        className="block w-full text-left rounded-2xl bg-[var(--sk-surface)] p-4 hover:bg-white/[0.07] transition-colors"
        data-testid="highlights-search-fallback"
      >
        <div className="flex items-center gap-2 mb-2">
          <Youtube className="w-4 h-4 text-red-500" />
          <div className="text-[10px] font-black uppercase tracking-wider text-zinc-400">Match Highlights</div>
          <ExternalLink className="ml-auto w-3 h-3 text-zinc-500" />
        </div>
        <p className="text-[12px] text-zinc-300">
          Highlights aren&apos;t on YouTube yet. Tap to search — usually live within an hour of full-time.
        </p>
      </button>
    );
  }

  return (
    <div className="rounded-2xl bg-[var(--sk-surface)] overflow-hidden" data-testid="highlights-card">
      <div className="flex items-center gap-2 px-4 pt-3.5 pb-2.5">
        <Youtube className="w-4 h-4 text-red-500" />
        <div className="text-[10px] font-black uppercase tracking-wider text-zinc-400">Match Highlights</div>
        <button
          type="button"
          onClick={() => openExternalUrl(`https://www.youtube.com/watch?v=${video.video_id}`)}
          className="ml-auto text-[10px] font-bold text-zinc-500 hover:text-zinc-300 flex items-center gap-1"
          data-testid="highlights-open-youtube"
        >
          YouTube <ExternalLink className="w-3 h-3" />
        </button>
      </div>

      {showVideo ? (
        <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
          <iframe
            className="absolute inset-0 w-full h-full"
            src={`${API_URL}/api/embed/youtube/${video.video_id}?autoplay=1`}
            title={video.title || `${homeTeam} vs ${awayTeam} highlights`}
            frameBorder="0"
            referrerPolicy="strict-origin-when-cross-origin"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            data-testid="highlights-iframe"
          />
        </div>
      ) : (
        <button
          type="button"
          onClick={video.embed_blocked ? () => openExternalUrl(`https://www.youtube.com/watch?v=${video.video_id}`) : onPlay}
          className="relative w-full block group"
          style={{ paddingBottom: '56.25%' }}
          aria-label={video.embed_blocked ? 'Watch highlights on YouTube' : 'Play highlights'}
          data-testid="highlights-play-button"
        >
          {video.thumbnail && (
            <img
              src={video.thumbnail}
              alt={video.title || 'Match highlights thumbnail'}
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
            />
          )}
          <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors flex items-center justify-center">
            <div className="w-14 h-14 rounded-full bg-red-600 group-hover:bg-red-500 flex items-center justify-center shadow-lg transition-colors">
              <Play className="w-6 h-6 text-white ml-0.5" fill="white" />
            </div>
          </div>
          {video.embed_blocked && (
            <div className="absolute bottom-2 left-0 right-0 flex justify-center">
              <span className="px-2.5 py-1 rounded-full bg-black/75 text-white text-[10px] font-bold flex items-center gap-1">
                <Youtube className="w-3 h-3 text-red-500" /> Watch on YouTube
              </span>
            </div>
          )}
        </button>
      )}

      <div className="px-4 py-3">
        {video.title && (
          <p className="text-[12px] font-bold text-white line-clamp-2 leading-snug">{video.title}</p>
        )}
        {video.channel && (
          <p className="text-[10px] text-zinc-500 mt-1">{video.channel}</p>
        )}
        {video.embed_blocked && !showVideo && (
          <p className="text-[10px] text-zinc-500 mt-1.5 flex items-center gap-1">
            <ExternalLink className="w-3 h-3" /> Opens in YouTube — the broadcaster blocks in-app playback for this clip.
          </p>
        )}
        {showVideo && (
          <button
            type="button"
            onClick={() => openExternalUrl(`https://www.youtube.com/watch?v=${video.video_id}`)}
            className="mt-2.5 flex items-center gap-1.5 text-[11px] font-bold text-zinc-400 hover:text-white transition-colors"
            data-testid="highlights-watch-on-youtube-fallback"
          >
            <Youtube className="w-3.5 h-3.5 text-red-500" />
            Video won&apos;t play? Watch on YouTube
            <ExternalLink className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
};

// ============== Player Stats Card (per-player, live) ==============
// Mirrors the FlashScore "Player Stats" view: a Home/Away team toggle, stat
// category tabs, and player rows sorted by match rating. Data updates live.
const PS_CATEGORIES = [
  { id: 'top', label: 'Top Stats', cols: [
    { key: 'shots_total', label: 'Shots' },
    { key: 'goals', label: 'Goals' },
    { key: 'assists', label: 'Assists' },
  ] },
  { id: 'shots', label: 'Shots', cols: [
    { key: 'shots_total', label: 'Shots' },
    { key: 'shots_on', label: 'On Target' },
  ] },
  { id: 'passes', label: 'Passes', cols: [
    { key: 'passes_total', label: 'Passes' },
    { key: 'key_passes', label: 'Key' },
    { key: 'pass_accuracy', label: 'Acc %' },
  ] },
  { id: 'defence', label: 'Defence', cols: [
    { key: 'tackles', label: 'Tackles' },
    { key: 'interceptions', label: 'Intercept' },
    { key: 'duels_won', label: 'Duels Won' },
  ] },
  { id: 'fouls', label: 'Fouls', cols: [
    { key: 'fouls_drawn', label: 'Won' },
    { key: 'fouls_committed', label: 'Committed' },
  ] },
];

const ratingClasses = (r) => {
  if (r == null) return 'bg-zinc-700 text-zinc-300';
  if (r >= 7.5) return 'bg-blue-600 text-white';
  if (r >= 7.0) return 'bg-emerald-600 text-white';
  if (r >= 6.5) return 'bg-amber-500 text-black';
  return 'bg-zinc-600 text-white';
};

// Build a "value / total (pct%)" string, hiding the fraction when there's no total.
const fmtFrac = (made, total) => {
  if (made == null && total == null) return null;
  if (total == null || total === 0) return `${made ?? 0}`;
  return `${made ?? 0}/${total} (${Math.round(((made ?? 0) / total) * 100)}%)`;
};

// Group a single player's match stats into labelled sections (only rows that
// actually have data from API-Football are shown — no permanent "–" walls).
const buildPlayerSections = (p) => {
  if (!p) return [];
  const row = (label, value) => ({ label, value });
  const has = (v) => v != null && v !== '';
  const sections = [
    { title: 'Attacking', rows: [
      has(p.goals) && row('Goals', p.goals),
      has(p.assists) && row('Assists', p.assists),
      has(p.shots_total) && row('Total shots', p.shots_total),
      has(p.shots_on) && row('Shots on target', p.shots_on),
      (has(p.dribbles_success) || has(p.dribbles_attempts)) && row('Successful dribbles', fmtFrac(p.dribbles_success, p.dribbles_attempts)),
      has(p.offsides) && row('Offsides', p.offsides),
    ] },
    { title: 'Passing', rows: [
      has(p.passes_total) && row('Passes', p.passes_total),
      has(p.pass_accuracy) && row('Pass accuracy', `${p.pass_accuracy}%`),
      has(p.key_passes) && row('Key passes', p.key_passes),
    ] },
    { title: 'Defending', rows: [
      (has(p.duels_won) || has(p.duels_total)) && row('Duels won', fmtFrac(p.duels_won, p.duels_total)),
      has(p.tackles) && row('Tackles', p.tackles),
      has(p.interceptions) && row('Interceptions', p.interceptions),
      has(p.blocks) && row('Blocks', p.blocks),
      has(p.dribbles_past) && row('Dribbled past', p.dribbles_past),
    ] },
    { title: 'Goalkeeping', rows: [
      has(p.saves) && row('Saves', p.saves),
      has(p.penalty_saved) && row('Penalties saved', p.penalty_saved),
    ] },
    { title: 'Discipline', rows: [
      has(p.fouls_drawn) && row('Fouls won', p.fouls_drawn),
      has(p.fouls_committed) && row('Fouls committed', p.fouls_committed),
      has(p.yellow) && row('Yellow cards', p.yellow),
      has(p.red) && row('Red cards', p.red),
      has(p.penalty_won) && row('Penalties won', p.penalty_won),
      has(p.penalty_committed) && row('Penalties conceded', p.penalty_committed),
    ] },
  ];
  return sections
    .map((s) => ({ ...s, rows: s.rows.filter(Boolean) }))
    .filter((s) => s.rows.length > 0);
};

// Bottom sheet showing one player's full in-match breakdown (FotMob-style).
const PlayerStatSheet = ({ player, teamName, teamLogo, open, onClose }) => {
  const sections = buildPlayerSections(player);
  return (
    <Drawer open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DrawerContent
        className="border-white/10 bg-[#0e1525] max-h-[85vh]"
        data-testid="player-stat-sheet"
      >
        {player && (
          <>
            {/* Header */}
            <div className="flex items-center gap-3 px-4 pt-2 pb-3 border-b border-white/10">
              {player.photo
                ? <img src={player.photo} alt="" className="w-14 h-14 rounded-full object-cover bg-zinc-800 shrink-0" />
                : <div className="w-14 h-14 rounded-full bg-zinc-800 shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="text-[17px] font-black text-white truncate">{player.name}{player.captain ? ' (C)' : ''}</div>
                <div className="text-[11px] text-zinc-400 truncate flex items-center gap-1.5">
                  {teamLogo && <img src={teamLogo} alt="" className="w-3.5 h-3.5 object-contain" />}
                  <span className="truncate">{player.position || '—'}{player.substitute ? ' · sub' : ''}{teamName ? ` · ${teamName}` : ''}</span>
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-[9px] font-black uppercase tracking-wider text-zinc-500">Rating</div>
                <span className={`inline-block mt-0.5 text-[15px] font-black px-2 py-0.5 rounded ${ratingClasses(player.rating)}`}>
                  {player.rating != null ? player.rating.toFixed(1) : '–'}
                </span>
              </div>
              <DrawerClose asChild>
                <button className="shrink-0 w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors" aria-label="Close" data-testid="player-stat-sheet-close">
                  <X className="w-4 h-4 text-zinc-300" />
                </button>
              </DrawerClose>
            </div>

            {/* Minutes chip */}
            {player.minutes != null && (
              <div className="px-4 pt-3">
                <span className="inline-block text-[11px] font-bold text-zinc-300 bg-white/5 rounded-full px-3 py-1">
                  {player.minutes}′ played{player.number != null ? ` · #${player.number}` : ''}
                </span>
              </div>
            )}

            {/* Stat sections */}
            <div className="px-4 py-3 overflow-y-auto">
              {sections.length === 0 ? (
                <div className="text-center text-[12px] text-zinc-500 py-8">Detailed stats not available yet for this player.</div>
              ) : sections.map((s) => (
                <div key={s.title} className="mb-4 last:mb-2">
                  <div className="text-[11px] font-black uppercase tracking-wider text-blue-400 mb-1.5">{s.title}</div>
                  {s.rows.map((r) => (
                    <div key={r.label} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                      <span className="text-[13px] text-zinc-300">{r.label}</span>
                      <span className="text-[13px] font-bold text-white tabular-nums">{r.value}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </>
        )}
      </DrawerContent>
    </Drawer>
  );
};

const PlayerStatsCard = ({ data, detail, isLive, isFinished }) => {
  const [team, setTeam] = useState('home');
  const [cat, setCat] = useState('top');
  const [selected, setSelected] = useState(null);

  // Only relevant once a match is live or finished.
  if (!isLive && !isFinished) return null;

  const teams = data?.teams || [];
  const wantedId = team === 'home' ? detail.home_id : detail.away_id;
  const teamData = teams.find((t) => t.team_id === wantedId) || teams[team === 'home' ? 0 : 1];
  const players = (teamData?.players || []).filter((p) => p.minutes != null || p.rating != null);
  const category = PS_CATEGORIES.find((c) => c.id === cat) || PS_CATEGORIES[0];

  // Star Man — highest match rating across BOTH teams (recomputed each poll,
  // so it can change live as the game develops).
  const starMan = teams
    .flatMap((t) => (t.players || []).map((p) => ({ ...p, _teamName: t.team_name, _teamLogo: t.team_logo })))
    .filter((p) => p.rating != null)
    .sort((a, b) => b.rating - a.rating)[0] || null;

  const shareStarMan = async () => {
    if (!starMan) return;
    const imgUrl = `${API_URL}/api/live-scores/match/${detail.fixture_id}/star-man-card.png`;
    const text = `⭐ Star Man: ${starMan.name} (${starMan.rating.toFixed(1)}) — ${detail.home_team} v ${detail.away_team} · via SmartKick`;
    // Best: share the actual image file (native image share to IG / WhatsApp / X).
    if (typeof navigator !== 'undefined' && navigator.canShare) {
      try {
        const res = await fetch(imgUrl);
        if (res.ok) {
          const blob = await res.blob();
          const file = new File([blob], 'smartkick-star-man.png', { type: 'image/png' });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file], title: 'Star Man', text });
            return;
          }
        }
      } catch { /* fall through */ }
    }
    // Fallback: native share sheet / clipboard with a link.
    const r = await shareNative({ title: 'Star Man', text, url: 'https://smartkick.co.uk/live-scores', dialogTitle: 'Share Star Man' });
    if (r.method === 'clipboard') toast.success('Copied to clipboard');
    else if (!r.shared && !r.cancelled) toast('Sharing not available on this device');
  };

  return (
    <div className="rounded-2xl bg-[var(--sk-surface)] overflow-hidden" data-testid="player-stats-card">
      <div className="flex items-center gap-1.5 px-4 pt-4 pb-2">
        <Users className="w-3.5 h-3.5 text-accent" />
        <div className="text-[10px] font-black uppercase tracking-wider text-zinc-500">Player Stats</div>
        {isLive && <span className="ml-auto flex items-center gap-1 text-[10px] font-black text-[var(--sk-live)]"><span className="live-dot" /> LIVE</span>}
      </div>

      {/* Star Man — top-rated player of the match */}
      {starMan && (
        <div
          className="mx-3 mb-2 rounded-xl bg-gradient-to-r from-amber-500/15 to-amber-500/[0.04] border border-amber-500/30 p-3 flex items-center gap-3"
          data-testid="player-stats-star-man"
        >
          <div className="relative shrink-0">
            {starMan.photo
              ? <img src={starMan.photo} alt="" className="w-11 h-11 rounded-full object-cover bg-zinc-800" loading="lazy" />
              : <div className="w-11 h-11 rounded-full bg-zinc-800" />}
            <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-amber-400 flex items-center justify-center ring-2 ring-[var(--sk-surface)]">
              <Star className="w-3 h-3 text-black" fill="black" />
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[9px] font-black uppercase tracking-wider text-amber-400">{isLive ? 'Star Man (so far)' : 'Star Man'}</div>
            <div className="text-[13px] font-bold text-white truncate">{starMan.name}</div>
            <div className="text-[10px] text-zinc-400 truncate flex items-center gap-1">
              {starMan._teamLogo && <img src={starMan._teamLogo} alt="" className="w-3 h-3 object-contain shrink-0" />}
              <span className="truncate">{starMan._teamName}{starMan.position ? ` · ${starMan.position}` : ''}</span>
            </div>
          </div>
          <span className={`text-[15px] font-black px-2 py-1 rounded shrink-0 ${ratingClasses(starMan.rating)}`}>
            {starMan.rating.toFixed(1)}
          </span>
          <button
            type="button"
            onClick={shareStarMan}
            className="shrink-0 w-9 h-9 rounded-full bg-amber-400 hover:bg-amber-300 flex items-center justify-center transition-colors"
            aria-label="Share Star Man"
            data-testid="share-star-man-btn"
          >
            <Share2 className="w-4 h-4 text-black" />
          </button>
        </div>
      )}

      {/* Home / Away toggle */}
      <div className="px-3 grid grid-cols-2 gap-1.5">
        {['home', 'away'].map((side) => {
          const name = side === 'home' ? detail.home_team : detail.away_team;
          const logo = side === 'home' ? detail.home_logo : detail.away_logo;
          return (
            <button
              key={side}
              onClick={() => setTeam(side)}
              className={`flex items-center justify-center gap-1.5 py-2 rounded-lg text-[12px] font-bold truncate ${team === side ? 'bg-accent text-white' : 'bg-white/5 text-zinc-400'}`}
              data-testid={`player-stats-team-${side}`}
            >
              {logo && <img src={logo} alt="" className="w-4 h-4 object-contain shrink-0" />}
              <span className="truncate">{name}</span>
            </button>
          );
        })}
      </div>

      {/* Category tabs */}
      <div className="px-3 mt-2 flex gap-1.5 overflow-x-auto no-scrollbar">
        {PS_CATEGORIES.map((c) => (
          <button
            key={c.id}
            onClick={() => setCat(c.id)}
            className={`shrink-0 px-3 py-1.5 rounded-full text-[11px] font-bold ${cat === c.id ? 'bg-white/15 text-white' : 'bg-white/5 text-zinc-400'}`}
            data-testid={`player-stats-cat-${c.id}`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* Table */}
      {players.length === 0 ? (
        <div className="text-center text-[12px] text-zinc-500 py-8 px-4">
          {isLive ? 'Player stats will appear shortly after kick-off.' : 'Player stats not available for this match.'}
        </div>
      ) : (
        <div className="mt-2">
          {/* Header row */}
          <div
            className="grid items-center gap-1 px-4 py-2 text-[9px] font-bold uppercase tracking-wider text-zinc-500 border-b border-[var(--sk-border)]"
            style={{ gridTemplateColumns: `minmax(0,1fr) 36px repeat(${category.cols.length}, minmax(40px, 1fr))` }}
          >
            <span>Player <span className="text-accent normal-case font-medium tracking-normal">· tap for full stats</span></span>
            <span className="text-center">Rat</span>
            {category.cols.map((col) => <span key={col.key} className="text-center leading-tight">{col.label}</span>)}
          </div>
          {players.map((p, idx) => (
            <div
              key={`${p.player_id ?? 'p'}-${idx}`}
              role="button"
              tabIndex={0}
              onClick={() => setSelected(p)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelected(p); } }}
              className="grid items-center gap-1 px-4 py-2 border-b border-[var(--sk-border)]/50 last:border-0 cursor-pointer hover:bg-white/[0.03] transition-colors"
              style={{ gridTemplateColumns: `minmax(0,1fr) 36px repeat(${category.cols.length}, minmax(40px, 1fr))` }}
              data-testid={`player-stats-row-${p.player_id}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                {p.photo && <img src={p.photo} alt="" className="w-7 h-7 rounded-full object-cover bg-zinc-800 shrink-0" loading="lazy" />}
                <div className="min-w-0">
                  <div className="text-[12px] text-white truncate font-medium">{p.name}{p.captain ? ' (C)' : ''}</div>
                  <div className="text-[10px] text-zinc-500 truncate">{p.position || '—'}{p.substitute ? ' · sub' : ''}</div>
                </div>
              </div>
              <div className="flex justify-center">
                <span className={`text-[11px] font-black px-1.5 py-0.5 rounded ${ratingClasses(p.rating)}`}>
                  {p.rating != null ? p.rating.toFixed(1) : '–'}
                </span>
              </div>
              {category.cols.map((col) => (
                <span key={col.key} className="text-center text-[12px] text-zinc-300 tabular-nums">
                  {p[col.key] != null && p[col.key] !== '' ? p[col.key] : '–'}
                </span>
              ))}
            </div>
          ))}
        </div>
      )}
      <PlayerStatSheet
        player={selected}
        teamName={teamData?.team_name}
        teamLogo={teamData?.team_logo}
        open={!!selected}
        onClose={() => setSelected(null)}
      />
    </div>
  );
};

// ============== Live Stats Card ==============
// API-Football returns one entry per team:
// [{ team_id, team_name, statistics: [{type:"Ball Possession", value:"55%"}, ...] }]
// We flatten and pair up the same stat type from each team into rows.
const STAT_DISPLAY_ORDER = [
  { key: 'Ball Possession', label: 'Possession', isPct: true },
  { key: 'Total Shots', label: 'Shots' },
  { key: 'Shots on Goal', label: 'Shots on Target' },
  { key: 'Shots off Goal', label: 'Shots off Target' },
  { key: 'Corner Kicks', label: 'Corners' },
  { key: 'Fouls', label: 'Fouls' },
  { key: 'Yellow Cards', label: 'Yellow Cards' },
  { key: 'Red Cards', label: 'Red Cards' },
  { key: 'Offsides', label: 'Offsides' },
  { key: 'Goalkeeper Saves', label: 'Saves' },
  { key: 'Total passes', label: 'Passes' },
  { key: 'Passes accurate', label: 'Accurate Passes' },
];

const parseStatValue = (v) => {
  if (v == null) return 0;
  if (typeof v === 'number') return v;
  const s = String(v).replace('%', '').trim();
  const n = parseInt(s, 10);
  return isNaN(n) ? 0 : n;
};

const LiveStatsCard = ({ stats, isLive, isFinished, homeTeamName, awayTeamName }) => {
  // The backend returns statistics either as
  //   (a) an array [{team_name, statistics: [{type, value}, …]}, …] — legacy shape, OR
  //   (b) a dict   {team_name: {stat_key: stat_value, …}, …}        — current shape.
  // Normalise to the array shape before doing anything else, otherwise
  // accessing `stats[0].statistics` blows up the whole match-detail
  // page with a TypeError that takes the back button down with it.
  const normalised = (() => {
    if (Array.isArray(stats)) return stats;
    if (stats && typeof stats === 'object') {
      // Preserve the home/away ordering from the scoreboard so the
      // first column in the UI always matches the home crest.
      const entries = Object.entries(stats);
      const sorted = [];
      const lookup = new Map(entries);
      if (homeTeamName && lookup.has(homeTeamName)) {
        sorted.push([homeTeamName, lookup.get(homeTeamName)]);
        lookup.delete(homeTeamName);
      }
      if (awayTeamName && lookup.has(awayTeamName)) {
        sorted.push([awayTeamName, lookup.get(awayTeamName)]);
        lookup.delete(awayTeamName);
      }
      for (const [k, v] of lookup) sorted.push([k, v]);
      return sorted.map(([name, obj]) => ({
        team_name: name,
        statistics: Object.entries(obj || {}).map(([type, value]) => ({ type, value })),
      }));
    }
    return [];
  })();

  if (!normalised || normalised.length < 2) {
    if (isLive) {
      return (
        <div className="rounded-2xl bg-[var(--sk-surface)] p-4" data-testid="live-stats-card">
          <div className="flex items-center gap-1.5 mb-3">
            <ActivityIcon className="w-3.5 h-3.5 text-accent" />
            <div className="text-[10px] font-black uppercase tracking-wider text-zinc-500">Match Stats</div>
            <span className="ml-auto flex items-center gap-1 text-[10px] font-black text-[var(--sk-live)]"><span className="live-dot" /> LIVE</span>
          </div>
          <div className="text-[11px] text-zinc-500 py-2">Waiting for stats from broadcast feed…</div>
        </div>
      );
    }
    if (!isFinished) {
      // Pre-match — don't render anything; tracker section is enough
      return null;
    }
    return (
      <div className="rounded-2xl bg-[var(--sk-surface)] p-4" data-testid="live-stats-card">
        <div className="flex items-center gap-1.5 mb-3">
          <ActivityIcon className="w-3.5 h-3.5 text-accent" />
          <div className="text-[10px] font-black uppercase tracking-wider text-zinc-500">Match Stats</div>
        </div>
        <div className="text-[11px] text-zinc-500 py-2">Stats not available for this match.</div>
      </div>
    );
  }

  const home = normalised[0];
  const away = normalised[1];
  const homeMap = new Map((home.statistics || []).map((s) => [s.type, s.value]));
  const awayMap = new Map((away.statistics || []).map((s) => [s.type, s.value]));

  const rows = STAT_DISPLAY_ORDER
    .map(({ key, label, isPct }) => {
      const hRaw = homeMap.get(key);
      const aRaw = awayMap.get(key);
      if (hRaw == null && aRaw == null) return null;
      const h = parseStatValue(hRaw);
      const a = parseStatValue(aRaw);
      if (h === 0 && a === 0) return null;
      const total = h + a || 1;
      const hPct = isPct ? h : Math.round((h / total) * 100);
      return { key, label, hRaw: hRaw ?? '0', aRaw: aRaw ?? '0', hPct, isPct };
    })
    .filter(Boolean);

  if (rows.length === 0) {
    return null;
  }

  return (
    <div className="rounded-2xl bg-[var(--sk-surface)] p-4" data-testid="live-stats-card">
      <div className="flex items-center gap-1.5 mb-3">
        <ActivityIcon className="w-3.5 h-3.5 text-accent" />
        <div className="text-[10px] font-black uppercase tracking-wider text-zinc-500">Match Stats</div>
        {isLive && <span className="ml-auto flex items-center gap-1 text-[10px] font-black text-[var(--sk-live)]"><span className="live-dot" /> LIVE</span>}
      </div>
      <div className="space-y-3">
        {rows.map((r) => (
          <div key={r.key}>
            <div className="flex items-center justify-between text-[11px] mb-1">
              <span className="font-bold text-white tabular-nums w-10 text-left">{r.hRaw}</span>
              <span className="text-zinc-400 text-[10px] uppercase tracking-wider font-bold">{r.label}</span>
              <span className="font-bold text-white tabular-nums w-10 text-right">{r.aRaw}</span>
            </div>
            <div className="h-1.5 rounded-full bg-black/40 overflow-hidden flex">
              <div
                className="h-full bg-[var(--sk-accent)] transition-all"
                style={{ width: `${r.hPct}%` }}
              />
              <div
                className="h-full bg-amber-500/80 transition-all"
                style={{ width: `${100 - r.hPct}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============== Odds Comparison Card (free, every match) ==============
const shortName = (name) => {
  if (!name) return '';
  const parts = String(name).trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 3).toUpperCase();
  return parts.map((p) => p[0]).join('').slice(0, 3).toUpperCase();
};

const normBookie = (n) => (n || '').toLowerCase().replace(/[^a-z0-9]/g, '');
const REMEMBER_BOOKIE_KEY = 'sk_odds_bookie';
// Fallback homepages so "Bet now" always works even before an affiliate deal
// is added in admin. Once an admin offer with a matching name exists, its
// affiliate cta_url + click tracking take over automatically.
const BOOKIE_HOME = {
  bet365: 'https://www.bet365.com', williamhill: 'https://www.williamhill.com',
  ladbrokes: 'https://www.ladbrokes.com', pinnacle: 'https://www.pinnacle.com',
  bwin: 'https://www.bwin.com', skybet: 'https://www.skybet.com',
  betfair: 'https://www.betfair.com', unibet: 'https://www.unibet.co.uk',
  paddypower: 'https://www.paddypower.com', coral: 'https://www.coral.co.uk',
  betvictor: 'https://www.betvictor.com', betway: 'https://www.betway.com',
  boylesports: 'https://www.boylesports.com', virginbet: 'https://www.virginbet.com',
  leovegas: 'https://www.leovegas.com', mrgreen: 'https://www.mrgreen.com',
  '888sport': 'https://www.888sport.com', betfred: 'https://www.betfred.com',
};

// UniScore-style odds: pick a market (1X2 / Over-Under / BTTS) and choose which
// bookmaker's prices to view, with the market-best price highlighted.
const OddsComparisonCard = ({ odds, isLive, onCtaClick }) => {
  const bookmakers = useMemo(() => odds?.bookmakers || [], [odds]);
  const best = odds?.best_odds || null;

  // Which markets actually have prices (only show tabs we can fill).
  const markets = useMemo(() => {
    const out = [];
    if (bookmakers.some((b) => (b.markets?.match_winner?.home || 0) > 0)) out.push({ id: '1x2', label: '1X2' });
    if (bookmakers.some((b) => (b.markets?.over_25 || 0) > 0 || (b.markets?.under_25 || 0) > 0)) out.push({ id: 'ou', label: 'Over/Under' });
    if (bookmakers.some((b) => (b.markets?.btts_yes || 0) > 0 || (b.markets?.btts_no || 0) > 0)) out.push({ id: 'btts', label: 'BTTS' });
    if (bookmakers.some((b) => (b.markets?.handicap_home || 0) > 0 || (b.markets?.handicap_away || 0) > 0)) out.push({ id: 'ah', label: 'Handicap' });
    return out;
  }, [bookmakers]);

  const [market, setMarket] = useState('1x2');
  const [bookieIdx, setBookieIdx] = useState(0);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [affiliates, setAffiliates] = useState({});
  const [remembered] = useState(() => { try { return localStorage.getItem(REMEMBER_BOOKIE_KEY); } catch { return null; } });
  const pickerRef = useRef(null);

  // Affiliate links — map admin-managed bookmaker offers by normalised name.
  useEffect(() => {
    let alive = true;
    fetch(`${API_URL}/api/scores/bookmakers`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!alive || !d?.offers) return;
        const map = {};
        d.offers.forEach((o) => { if (o.cta_url) map[normBookie(o.name)] = { url: o.cta_url, id: o.id }; });
        setAffiliates(map);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const betLink = useCallback((name) => {
    const k = normBookie(name);
    const aff = affiliates[k];
    return { url: aff?.url || BOOKIE_HOME[k] || null, offerId: aff?.id || null };
  }, [affiliates]);

  useEffect(() => {
    if (markets.length && !markets.some((m) => m.id === market)) setMarket(markets[0].id);
  }, [markets, market]);

  // Preselect the visitor's remembered bookmaker; otherwise the best home price.
  useEffect(() => {
    if (!bookmakers.length) return;
    if (remembered) {
      const i = bookmakers.findIndex((b) => normBookie(b.name) === normBookie(remembered));
      if (i >= 0) { setBookieIdx(i); return; }
    }
    let bi = 0; let bv = -1;
    bookmakers.forEach((b, i) => { const v = b.markets?.match_winner?.home || 0; if (v > bv) { bv = v; bi = i; } });
    setBookieIdx(bi);
  }, [bookmakers, remembered]);

  const chooseBookie = (i) => {
    setBookieIdx(i);
    setPickerOpen(false);
    try { localStorage.setItem(REMEMBER_BOOKIE_KEY, bookmakers[i]?.name || ''); } catch { /* ignore */ }
  };

  useEffect(() => {
    const onDoc = (e) => { if (pickerRef.current && !pickerRef.current.contains(e.target)) setPickerOpen(false); };
    if (pickerOpen) document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [pickerOpen]);

  // Columns for the selected market (label + value + market-best value).
  const cols = useMemo(() => {
    const b = bookmakers[bookieIdx];
    if (!b) return [];
    if (market === '1x2') {
      const m = b.markets?.match_winner || {};
      return [
        { key: 'home', label: shortName(odds?.home_team), value: m.home, best: best?.home_win },
        { key: 'draw', label: 'X', value: m.draw, best: best?.draw },
        { key: 'away', label: shortName(odds?.away_team), value: m.away, best: best?.away_win },
      ];
    }
    if (market === 'ou') {
      return [
        { key: 'over', label: 'Over 2.5', value: b.markets?.over_25, best: best?.over_25 },
        { key: 'under', label: 'Under 2.5', value: b.markets?.under_25, best: best?.under_25 },
      ];
    }
    if (market === 'ah') {
      const pt = (p) => (p == null ? '' : (p > 0 ? `+${p}` : `${p}`));
      return [
        { key: 'hch', label: `${shortName(odds?.home_team)} ${pt(b.markets?.handicap_home_point)}`.trim(), value: b.markets?.handicap_home, best: null },
        { key: 'ach', label: `${shortName(odds?.away_team)} ${pt(b.markets?.handicap_away_point)}`.trim(), value: b.markets?.handicap_away, best: null },
      ];
    }
    return [
      { key: 'yes', label: 'BTTS Yes', value: b.markets?.btts_yes, best: best?.btts_yes },
      { key: 'no', label: 'BTTS No', value: b.markets?.btts_no, best: best?.btts_no },
    ];
  }, [bookmakers, bookieIdx, market, odds, best]);

  const selected = bookmakers[bookieIdx];

  return (
    <div className="rounded-2xl border border-blue-500/25 bg-gradient-to-br from-blue-500/6 to-transparent p-4" data-testid="odds-card">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-7 h-7 rounded-lg bg-blue-500/20 border border-blue-500/40 flex items-center justify-center">
          <TrendingUp className="w-3.5 h-3.5 text-blue-300" />
        </div>
        <div className="text-[10px] font-black tracking-[0.18em] uppercase text-blue-300">
          {isLive ? 'In-Play Odds' : 'Odds Comparison'}
        </div>
        {isLive && (
          <span className="inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider text-[var(--sk-live)]" data-testid="odds-live-badge">
            <span className="live-dot" /> LIVE
          </span>
        )}
        {bookmakers.length > 0 && (
          <span className="ml-auto text-[10px] text-zinc-500">{bookmakers.length} books</span>
        )}
      </div>

      {bookmakers.length === 0 ? (
        <div className="text-[11px] text-zinc-500 py-2">
          {isLive
            ? 'In-play prices will appear here while the bookmakers are trading this match.'
            : 'Odds will appear closer to kick-off, once the major UK bookmakers publish their lines.'}
        </div>
      ) : (
        <>
          {/* Market tabs + bookmaker picker */}
          <div className="flex items-center gap-1.5 mb-3">
            <div className="flex gap-1 overflow-x-auto no-scrollbar flex-1" data-testid="odds-market-tabs">
              {markets.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setMarket(m.id)}
                  className={`shrink-0 px-3 py-1.5 rounded-full text-[11px] font-bold transition-colors ${market === m.id ? 'bg-blue-500 text-white' : 'bg-white/5 text-zinc-400 hover:bg-white/10'}`}
                  data-testid={`odds-market-${m.id}`}
                >
                  {m.label}
                </button>
              ))}
            </div>
            {/* Bookmaker picker */}
            <div className="relative shrink-0" ref={pickerRef}>
              <button
                type="button"
                onClick={() => setPickerOpen((o) => !o)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/15 text-[11px] font-bold text-white max-w-[130px] transition-colors"
                data-testid="odds-bookie-picker"
              >
                <span className="truncate">{selected?.name || 'Bookmaker'}</span>
                <ChevronDown className={`w-3.5 h-3.5 shrink-0 transition-transform ${pickerOpen ? 'rotate-180' : ''}`} />
              </button>
              {pickerOpen && (
                <div className="absolute right-0 top-full mt-1.5 w-52 max-h-72 overflow-y-auto rounded-xl bg-[#0f1420] border border-white/15 shadow-2xl z-30 py-1" data-testid="odds-bookie-list">
                  {bookmakers.map((b, i) => (
                    <button
                      key={`${b.name}-${i}`}
                      type="button"
                      onClick={() => chooseBookie(i)}
                      className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-[12px] transition-colors ${i === bookieIdx ? 'bg-blue-500/20 text-white' : 'text-zinc-300 hover:bg-white/5'}`}
                      data-testid={`odds-bookie-option-${i}`}
                    >
                      <span className="truncate">{b.name}</span>
                      {i === bookieIdx && <Check className="w-3.5 h-3.5 text-blue-300 shrink-0" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Selected bookmaker's prices for the chosen market (decimal, UniScore-style).
              Each price is tappable → bookmaker's affiliate page (Bet now). */}
          <div className={`grid gap-1.5 ${cols.length === 3 ? 'grid-cols-3' : 'grid-cols-2'}`} data-testid="odds-selected-row">
            {cols.map((c) => {
              const isBest = c.value > 0 && best && c.value === c.best;
              const { url, offerId } = betLink(selected?.name);
              const clickable = c.value > 0 && !!url;
              const onBet = () => {
                if (!clickable) return;
                if (offerId) fetch(`${API_URL}/api/scores/bookmakers/${offerId}/click`, { method: 'POST' }).catch(() => {});
                window.open(url, '_blank', 'noopener,noreferrer');
              };
              return (
                <button
                  type="button"
                  key={c.key}
                  onClick={onBet}
                  disabled={!clickable}
                  className={`rounded-xl px-2 py-2.5 text-center border transition-all ${isBest ? 'bg-emerald-500/15 border-emerald-500/40' : 'bg-black/30 border-white/10'} ${clickable ? 'hover:border-emerald-400/60 active:scale-[0.97] cursor-pointer' : 'cursor-default opacity-90'}`}
                  data-testid={`odds-cell-${c.key}`}
                >
                  <div className="text-[9px] text-zinc-400 uppercase tracking-wider font-bold truncate">{c.label}</div>
                  <div className={`text-[18px] font-black tabular-nums mt-0.5 ${isBest ? 'text-emerald-300' : 'text-white'}`}>
                    {c.value > 0 ? Number(c.value).toFixed(2) : '-'}
                  </div>
                  {clickable && (
                    <div className="text-[8px] font-black text-emerald-400 uppercase tracking-[0.12em] mt-0.5">Bet now ↗</div>
                  )}
                </button>
              );
            })}
          </div>

          <p className="text-[10px] text-zinc-500 mt-2 leading-snug">
            Showing <span className="text-zinc-300 font-semibold">{selected?.name}</span> · tap a price to bet · best market price in green · tap the bookmaker to switch · 18+ · BeGambleAware.org
          </p>
        </>
      )}

      {/* Soft funnel — not blocking the data */}
      <button
        type="button"
        onClick={() => onCtaClick?.()}
        className="mt-3 w-full flex items-center justify-center gap-1.5 py-2.5 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/40 text-emerald-300 text-[12px] font-bold transition-colors"
        data-testid="odds-predictions-cta"
      >
        <Zap className="w-3.5 h-3.5" />
        Want an AI pick for this match?
        <ArrowRight className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

const EventIcon = ({ type, detail }) => {
  const t = (type || '').toLowerCase();
  const d = (detail || '').toLowerCase();
  if (t === 'subst') {
    return (
      <span className="inline-flex flex-col leading-none shrink-0" title="Substitution">
        <ArrowUp className="w-2.5 h-2.5 text-emerald-400" strokeWidth={3} />
        <ArrowDown className="w-2.5 h-2.5 text-rose-400 -mt-0.5" strokeWidth={3} />
      </span>
    );
  }
  if (t === 'card') {
    return <span className={`inline-block w-2.5 h-3.5 rounded-[2px] shrink-0 ${d.includes('red') ? 'bg-red-500' : 'bg-yellow-400'}`} title={detail} />;
  }
  if (t === 'goal') {
    const isOwn = d.includes('own');
    return (
      <span className={`inline-flex items-center justify-center w-4 h-4 rounded-full shrink-0 text-[9px] ${isOwn ? 'bg-rose-500/20' : 'bg-white/10'}`} title={detail || 'Goal'}>
        <span aria-hidden>⚽</span>
        {d.includes('penalty') && <span className="text-[7px] font-black text-zinc-300 ml-0.5">P</span>}
      </span>
    );
  }
  if (t === 'var') return <span className="text-[8px] font-black text-zinc-400 shrink-0" title="VAR">VAR</span>;
  return <span className="w-1.5 h-1.5 rounded-full bg-zinc-500 inline-block shrink-0" />;
};

export default ScoresMatchDetail;
